import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import api from "@/lib/api";

// Remembers scroll position per history entry so Back returns the user to exactly
// where they were — even when the destination reloads its content asynchronously.
const positions = new Map(); // history key -> scrollY

export default function ScrollManager() {
    const location = useLocation();
    const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
    const keyRef = useRef(location.key);
    const restoringRef = useRef(false);

    // Site-visit analytics — one beacon per pathname change (fire-and-forget).
    useEffect(() => {
        api.post("/analytics/track", { type: "visit", path: location.pathname }).catch(() => {});
    }, [location.pathname]);

    // Record the scroll offset for the current entry — but NOT while we're
    // programmatically restoring (that would overwrite the target with transient
    // mid-scroll values) and never in cleanup (which fires at scrollY 0 on remount).
    useEffect(() => {
        keyRef.current = location.key;
        let ticking = false;
        const onScroll = () => {
            if (restoringRef.current || ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                positions.set(keyRef.current, window.scrollY);
                ticking = false;
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [location.key]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";

        // A hash target (e.g. #post-123) is handled by useHashScroll — don't fight it.
        if (location.hash) return;

        if (navType === "POP") {
            const target = positions.get(location.key);
            if (!target) { window.scrollTo(0, 0); return; }

            // Restore, retrying as async content grows the page (up to 5s), and bail
            // the moment the visitor scrolls so we never fight them.
            restoringRef.current = true;
            let userScrolled = false;
            const start = Date.now();
            const onUser = () => { userScrolled = true; };
            window.addEventListener("wheel", onUser, { passive: true });
            window.addEventListener("touchmove", onUser, { passive: true });
            window.addEventListener("keydown", onUser);
            let raf;
            const stop = () => {
                restoringRef.current = false;
                if (raf) cancelAnimationFrame(raf);
                window.removeEventListener("wheel", onUser);
                window.removeEventListener("touchmove", onUser);
                window.removeEventListener("keydown", onUser);
            };
            const step = () => {
                if (userScrolled) return stop();
                window.scrollTo(0, target);
                if (window.scrollY >= target - 2 || Date.now() - start > 5000) {
                    positions.set(location.key, target); // keep the value intact
                    return stop();
                }
                raf = requestAnimationFrame(step);
            };
            raf = requestAnimationFrame(step);
            return stop;
        }

        // New navigation (PUSH/REPLACE) without a hash → start at the top.
        window.scrollTo(0, 0);
    }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}
